/**
 * @category LeftSide
 * @component
 * @module Files/fileProcessing/RawAndSolved
 * @description This component is displaying 2 windows, one for raw file data (limited to 10 lines) and one for
 * processed data based on user selected options. If data is processed successfully, it is displayed as a table.
 */
import React, { useContext, useState, useEffect } from "react";
import { GeneralContext } from "../../../../context/GeneralContext";
import { DashboardContext } from "../../../../context/DashboardContext";
import { Select, MenuItem, FormControl } from "@mui/material";
import units from "../../../../utils/units";
import { convertDataFromUnitsToUnits } from "../../../../utils/helpers";
import "./fileProcessing.scss";

function RawAndSolved(props) {
  const {
    rawFileContent,
    processedContent,
    single = false,
    options = {},
    modalId,
  } = props;
  const presetName = options.presetName || "";
  const { recordedErrorLog } = useContext(GeneralContext);
  const { defaultXAxisUnit } = useContext(DashboardContext);
  const [displayUnit, setDisplayUnit] = useState(defaultXAxisUnit || units[0]);

  // Sync displayUnit when defaultXAxisUnit changes
  useEffect(() => {
    setDisplayUnit(defaultXAxisUnit);
  }, [defaultXAxisUnit]);

  /**
   * This function takes the content of a file and returns the first 10 lines of it.
   * @function getFirstTenLines
   * @param {string} content - This is the content of the file that is being processed.
   * @returns {string} - This is the first 10 lines of the file.
   */
  const getFirstTenLines = (content) => {
    try {
      if (content === null) {
        return "";
      }
      const lines = content.split("\n");
      return lines.slice(0, 10).join("\n");
    } catch (error) {
      recordedErrorLog("First 10 lines getter failure: ", error);
    }
  };

  const getFirstTenDataPoints = (dataPoints) => {
    try {
      if (dataPoints === null) {
        return "";
      }

      // Use preset name for Y column if available, otherwise use "Y"
      let lines = `X\tY\n`;

      for (let i = 0; i < dataPoints.length; i++) {
        const point = dataPoints[i];
        if (i >= 10) {
          break;
        }

        // Find the Y value - could be 'y' or preset name
        const yValue =
          point.y ||
          point[presetName] ||
          Object.values(point).find((val) => val !== point.x);
        lines = lines + `${point.x}\t${yValue}\n`;
      }

      return lines;
    } catch (error) {
      recordedErrorLog("First 10 data points getter failure: ", error);
    }
  };

  const getFailedMessage = () => {
    return (
      <div>
        <div>Could not process the file.</div>
        <div>
          If you want us to add new formats, please contact us and send an
          example file to:&nbsp;
          <a href="mailto:contact@speqqle.com">support@speqqle.ch</a>
        </div>
      </div>
    );
  };
  /**
   * This function takes the content of a file and returns a table containing the first 10 rows of the processed
   * data.
   * @function getTenTableRows
   * @param {object[]} content - This is the content of the file that has been processed.
   * @param {string[]} xykeys - The keys to display in the table
   * @returns {JSX.Element} - This is the table containing the processed data.
   */
  const getTenTableRows = (content, xykeys) => {
    try {
      // Return null if the content is empty
      if (content === "") {
        return null;
      }
      if (content === null || content.length === 0) {
        return getFailedMessage();
      }

      // Convert data to display unit if needed
      let displayContent = content;
      if (displayUnit !== units[0]) {
        // Extract x and y arrays for conversion
        // Convert all points at once
        displayContent = convertDataFromUnitsToUnits(
          units[0],
          displayUnit,
          content
        );

        // Map back to objects with all original properties
      }

      // Get the keys from the first object to define the table columns
      // Create the header row
      const unitName =
        typeof displayUnit.name === "string"
          ? displayUnit.name
          : displayUnit.value === "cm^-1"
          ? "cm⁻¹"
          : displayUnit.value;

      const headerRow = (
        <tr>
          {xykeys.map((key) => (
            <th key={key} style={{ textAlign: "center" }}>
              {key === "x" ? <span>x[{unitName}]</span> : key}
            </th>
          ))}
        </tr>
      );

      // Create the data rows (up to 10)
      const dataRows = displayContent
        .slice(0, 10)
        .map((item, index) => {
          // Check if any key is missing (undefined)
          const hasUndefined = xykeys.some((key) => item[key] === undefined);
          if (hasUndefined) {
            return null; // Will be filtered out
          }

          return (
            <tr key={index}>
              {xykeys.map((key) => (
                <td key={key}>{isNaN(item[key]) ? "NaN" : item[key]}</td>
              ))}
            </tr>
          );
        })
        .filter(Boolean); // Remove null entries

      // If any row had undefined values, return failed message
      if (dataRows.length < Math.min(displayContent.length, 10)) {
        return getFailedMessage();
      }

      // Construct the table
      return (
        <table className="processedTable">
          <thead>{headerRow}</thead>
          <tbody>{dataRows}</tbody>
        </table>
      );
    } catch (error) {
      recordedErrorLog("10 table rows getter failure: ", error);
    }
  };

  let xykeys = ["x"];
  if (options.presetName && options.presetName.trim() !== "") {
    xykeys.push(options.presetName);
  } else {
    xykeys.push("y");
  }

  // Add additional columns with their preset names
  if (options.additionalColumns && Array.isArray(options.additionalColumns)) {
    options.additionalColumns.forEach((column) => {
      if (column.presetName && column.presetName.trim() !== "") {
        xykeys.push(column.presetName);
      } else {
        xykeys.push("y");
      }
    });
  }

  const table = getTenTableRows(processedContent, xykeys);

  let statsLine = null;
  let largeDataWarning = null;
  if (
    processedContent &&
    Array.isArray(processedContent) &&
    processedContent.length >= 10
  ) {
    let processedContentInDisplayUnit = processedContent;

    if (displayUnit !== units[0]) {
      processedContentInDisplayUnit = convertDataFromUnitsToUnits(
        units[0],
        displayUnit,
        processedContent
      );
    }
    const xVals = processedContentInDisplayUnit
      .map((item) => item.x)
      .filter((x) => typeof x === "number" && !isNaN(x));
    if (xVals.length > 0) {
      const minX = Math.min(...xVals);
      const maxX = Math.max(...xVals);
      statsLine = (
        <div className="processedStats">
          {`Points: ${processedContent.length} | x min: ${minX} | x max: ${maxX}`}
        </div>
      );
      if (processedContent.length > 20000) {
        largeDataWarning = (
          <div style={{ color: "red", fontWeight: "bold", marginTop: "8px" }}>
            Warning: Large datasets may significantly slow down the application.
            For best performance, please use the “Pre-cut data” option to reduce
            the dataset size.
          </div>
        );
      }
    }
  }

  return (
    <div className="rawAndSolved">
      <div className="contentArea">
        <div className="title">Raw Data:</div>
        <pre className="content">
          {single
            ? getFirstTenDataPoints(rawFileContent)
            : getFirstTenLines(rawFileContent)}
        </pre>
      </div>
      <div className="contentArea">
        <div
          className="title"
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <span>Processed data in</span>
          <FormControl
            variant="standard"
            size="small"
            style={{ minWidth: "100px" }}
          >
            <Select
              MenuProps={{
                container: () => {
                  // Use modalId if provided (for single file reprocess), otherwise use fileProcessWindow
                  const containerId = modalId || "fileProcessWindow";
                  return document.getElementById(containerId);
                },
                PaperProps: {
                  style: {
                    zIndex: 10000,
                  },
                },
              }}
              value={displayUnit.value}
              onChange={(e) => {
                const selectedUnit = units.find(
                  (u) => u.value === e.target.value
                );
                if (selectedUnit) {
                  setDisplayUnit(selectedUnit);
                }
              }}
              className="display_unit_select"
              renderValue={(value) => {
                const selectedUnit = units.find((u) => u.value === value);
                if (!selectedUnit) return value;
                // Return the unit name (can be JSX or string)
                return selectedUnit.name;
              }}
            >
              {units.map((unit) => (
                <MenuItem key={unit.value} value={unit.value}>
                  {unit.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <span>:</span>
        </div>
        <div
          className={`content ${
            table !== null && typeof table !== "string" ? "noBorder" : ""
          }`}
        >
          {table}
        </div>
        {statsLine}
        {largeDataWarning}
      </div>
    </div>
  );
}

export default RawAndSolved;
